import { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import { tenantUsersAPI, testEquipmentAPI } from '../../api';
import { useAuthStore } from '../../store/authStore';
import { Tooltip, TooltipProvider } from '../../components/ui/Tooltip';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent } from '../../components/ui/Card';
import { FormLabel } from '../../components/ui/FormLabel';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import {
  UserPlus,
  RefreshCw,
  ShieldCheck,
  ShieldAlert,
  Users,
  Edit,
  Trash2,
  X,
  Plus,
} from 'lucide-react';

const ACCESS_FLAGS = [
  { key: 'isAdmin', label: 'Admin' },
  { key: 'isQs', label: 'QS' },
  { key: 'isEmployed', label: 'Employed' },
  { key: 'isContractor', label: 'Contractor' },
  { key: 'isApprentice', label: 'Apprentice' },
  { key: 'isOfficeStaff', label: 'Office' },
  { key: 'isDesigner', label: 'Designer' },
  { key: 'isInstaller', label: 'Installer' },
];

const initialFormState = {
  id: null,
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  role: 'tenant_user',
  password: '',
  confirmPassword: '',
  isActive: true,
  isAdmin: false,
  isQs: false,
  isEmployed: false,
  isContractor: false,
  isApprentice: false,
  isOfficeStaff: false,
  isDesigner: false,
  isInstaller: false,
  canAccessCertificates: true,
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return value;
  }
};

export default function TenantUserManagement() {
  const { user: currentUser, updateUser } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formState, setFormState] = useState(initialFormState);
  const [formMode, setFormMode] = useState('create');
  const [formOpen, setFormOpen] = useState(false);
  const [testEquipment, setTestEquipment] = useState([]);
  const [equipmentFormOpen, setEquipmentFormOpen] = useState(false);
  const [equipmentFormState, setEquipmentFormState] = useState({
    id: null,
    equipmentType: '',
    serialNumber: '',
    manufacturer: '',
    model: '',
    calibrationDate: '',
    nextCalibrationDue: '',
    notes: '',
  });

  const isEditing = formMode === 'edit';
  const editingSelf = isEditing && formState.id === currentUser?.id;

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await tenantUsersAPI.list();
      setUsers(response.users || []);
    } catch (err) {
      console.error('Failed to load users', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setFormState(initialFormState);
    setFormMode('create');
    setFormOpen(false);
    setIsSubmitting(false);
    setTestEquipment([]);
    setEquipmentFormOpen(false);
    setEquipmentFormState({
      id: null,
      equipmentType: '',
      serialNumber: '',
      manufacturer: '',
      model: '',
      calibrationDate: '',
      nextCalibrationDue: '',
      notes: '',
    });
  };

  const openCreateForm = () => {
    setFormMode('create');
    setFormState(initialFormState);
    setError('');
    setSuccess('');
    setFormOpen(true);
  };

  const openEditForm = async (user) => {
    setFormMode('edit');
    setFormState({
      id: user.id,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'tenant_user',
      password: '',
      confirmPassword: '',
      isActive: Boolean(user.isActive),
      isAdmin: Boolean(user.isAdmin),
      isQs: Boolean(user.isQs),
      isEmployed: Boolean(user.isEmployed),
      isContractor: Boolean(user.isContractor),
      isApprentice: Boolean(user.isApprentice),
      isOfficeStaff: Boolean(user.isOfficeStaff),
      isDesigner: Boolean(user.isDesigner),
      isInstaller: Boolean(user.isInstaller),
      canAccessCertificates: user.canAccessCertificates !== undefined ? Boolean(user.canAccessCertificates) : true,
    });
    setError('');
    setSuccess('');
    setFormOpen(true);
    
    // Load test equipment for this user
    try {
      const response = await testEquipmentAPI.getByUser(user.id);
      setTestEquipment(response.equipment || []);
    } catch (err) {
      console.error('Failed to load test equipment', err);
      setTestEquipment([]);
    }
  };

  const handleFormClose = () => {
    resetForm();
  };

  const handleInputChange = (key) => (event) => {
    setFormState((prev) => ({
      ...prev,
      [key]: event.target.value,
    }));
  };

  const handleCheckboxChange = (key) => (event) => {
    setFormState((prev) => ({
      ...prev,
      [key]: event.target.checked,
    }));
  };

  const validateForm = () => {
    if (!formState.firstName.trim() || !formState.lastName.trim()) {
      return 'First name and last name are required';
    }

    if (!formState.email.trim()) {
      return 'Email is required';
    }

    if (formMode === 'create') {
      if (!formState.password || formState.password.length < 8) {
        return 'Password must be at least 8 characters long';
      }
      if (formState.password !== formState.confirmPassword) {
        return 'Passwords do not match';
      }
    }

    if (formMode === 'edit' && formState.password) {
      if (formState.password.length < 8) {
        return 'Password must be at least 8 characters long';
      }
      if (formState.password !== formState.confirmPassword) {
        return 'Passwords do not match';
      }
    }

    if (editingSelf && formState.role !== 'tenant_admin') {
      return 'You cannot change your own role';
    }

    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {
      firstName: formState.firstName.trim(),
      lastName: formState.lastName.trim(),
      email: formState.email.trim(),
      phone: formState.phone ? formState.phone.trim() : null,
      role: formState.role,
      isActive: formState.isActive,
      isAdmin: formState.isAdmin,
      isQs: formState.isQs,
      isEmployed: formState.isEmployed,
      isContractor: formState.isContractor,
      isApprentice: formState.isApprentice,
      isOfficeStaff: formState.isOfficeStaff,
      isDesigner: formState.isDesigner,
      isInstaller: formState.isInstaller,
      canAccessCertificates: formState.role === 'tenant_admin' ? true : formState.canAccessCertificates,
    };

    if (formState.password) {
      payload.password = formState.password;
    }

    setIsSubmitting(true);

    try {
      if (formMode === 'create') {
        const response = await tenantUsersAPI.create(payload);
        setSuccess('User created successfully');
        setUsers((prev) => [...prev, response.user]);
      } else {
        const response = await tenantUsersAPI.update(formState.id, payload);
        setSuccess('User updated successfully');
        setUsers((prev) => prev.map((u) => (u.id === response.user.id ? response.user : u)));

        if (editingSelf && response.user) {
          updateUser({
            email: response.user.email,
            firstName: response.user.firstName,
            lastName: response.user.lastName,
            role: response.user.role,
            isAdmin: response.user.isAdmin,
            isQs: response.user.isQs,
            isEmployed: response.user.isEmployed,
            isContractor: response.user.isContractor,
            isApprentice: response.user.isApprentice,
            isOfficeStaff: response.user.isOfficeStaff,
            isDesigner: response.user.isDesigner,
            isInstaller: response.user.isInstaller,
            canAccessCertificates: response.user.canAccessCertificates,
          });
        }
      }

      handleFormClose();
      await loadUsers();
    } catch (err) {
      console.error('Failed to submit user form', err);
      setError(err.message || 'Failed to save user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivate = async (user) => {
    if (!confirm(`Deactivate ${user.firstName} ${user.lastName}? This can be undone later.`)) return;
    setError('');
    setSuccess('');

    try {
      await tenantUsersAPI.remove(user.id);
      setSuccess('User deactivated');
      await loadUsers();
    } catch (err) {
      console.error('Failed to deactivate user', err);
      setError(err.message || 'Failed to deactivate user');
    }
  };

  const openEquipmentForm = (equipment = null) => {
    if (equipment) {
      setEquipmentFormState({
        id: equipment.id,
        equipmentType: equipment.equipmentType || '',
        serialNumber: equipment.serialNumber || '',
        manufacturer: equipment.manufacturer || '',
        model: equipment.model || '',
        calibrationDate: equipment.calibrationDate || '',
        nextCalibrationDue: equipment.nextCalibrationDue || '',
        notes: equipment.notes || '',
      });
    } else {
      setEquipmentFormState({
        id: null,
        equipmentType: '',
        serialNumber: '',
        manufacturer: '',
        model: '',
        calibrationDate: '',
        nextCalibrationDue: '',
        notes: '',
      });
    }
    setEquipmentFormOpen(true);
  };

  const closeEquipmentForm = () => {
    setEquipmentFormOpen(false);
    setEquipmentFormState({
      id: null,
      equipmentType: '',
      serialNumber: '',
      manufacturer: '',
      model: '',
      calibrationDate: '',
      nextCalibrationDue: '',
      notes: '',
    });
  };

  const handleEquipmentInputChange = (key) => (event) => {
    setEquipmentFormState((prev) => ({
      ...prev,
      [key]: event.target.value,
    }));

    // Auto-set next calibration due date when calibration date changes
    if (key === 'calibrationDate' && event.target.value) {
      const calibrationDate = new Date(event.target.value);
      const nextDue = new Date(calibrationDate);
      nextDue.setFullYear(nextDue.getFullYear() + 1);
      setEquipmentFormState((prev) => ({
        ...prev,
        calibrationDate: event.target.value,
        nextCalibrationDue: nextDue.toISOString().split('T')[0],
      }));
    }
  };

  const handleEquipmentSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!equipmentFormState.serialNumber.trim()) {
      setError('Serial number is required');
      return;
    }

    if (!equipmentFormState.manufacturer.trim()) {
      setError('Manufacturer is required');
      return;
    }

    if (!equipmentFormState.equipmentType) {
      setError('Equipment type is required');
      return;
    }

    try {
      const payload = {
        userId: formState.id,
        equipmentType: equipmentFormState.equipmentType,
        serialNumber: equipmentFormState.serialNumber.trim(),
        manufacturer: equipmentFormState.manufacturer.trim(),
        model: equipmentFormState.model.trim() || null,
        calibrationDate: equipmentFormState.calibrationDate || null,
        nextCalibrationDue: equipmentFormState.nextCalibrationDue || null,
        notes: equipmentFormState.notes.trim() || null,
      };

      if (equipmentFormState.id) {
        await testEquipmentAPI.update(equipmentFormState.id, payload);
        setSuccess('Test equipment updated successfully');
      } else {
        await testEquipmentAPI.create(payload);
        setSuccess('Test equipment added successfully');
      }

      // Reload equipment list
      const response = await testEquipmentAPI.getByUser(formState.id);
      setTestEquipment(response.equipment || []);
      closeEquipmentForm();
    } catch (err) {
      console.error('Failed to save test equipment', err);
      setError(err.message || 'Failed to save test equipment');
    }
  };

  const handleDeleteEquipment = async (equipmentId) => {
    if (!confirm('Are you sure you want to delete this test equipment?')) return;
    setError('');
    setSuccess('');

    try {
      await testEquipmentAPI.delete(equipmentId);
      setSuccess('Test equipment deleted successfully');
      const response = await testEquipmentAPI.getByUser(formState.id);
      setTestEquipment(response.equipment || []);
    } catch (err) {
      console.error('Failed to delete test equipment', err);
      setError(err.message || 'Failed to delete test equipment');
    }
  };

  const activeCount = useMemo(
    () => users.filter((u) => u.isActive).length,
    [users]
  );

  const tenantAdminCount = useMemo(
    () => users.filter((u) => u.role === 'tenant_admin' && u.isActive).length,
    [users]
  );

  const roleOptions = useMemo(() => {
    if (isEditing && formState.role === 'tenant_admin') {
      return [
        { value: 'tenant_admin', label: 'Tenant Admin' },
      ];
    }
    return [
      { value: 'tenant_user', label: 'Tenant User' },
      { value: 'tenant_admin', label: 'Tenant Admin' },
    ];
  }, [formState.role, isEditing]);

  return (
    <TooltipProvider>
      <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">Team Members</h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Manage tenant admins and staff access to SparxMate
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge variant="neutral" className="text-micro">
                  <Users className="h-3.5 w-3.5 mr-1" />
                  Active users: {activeCount}
                </Badge>
                <Badge variant="success" className="text-micro">
                  <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                  Admins: {tenantAdminCount}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadUsers()}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={openCreateForm}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 rounded-[var(--ds-radius-md)] border border-[var(--ds-status-danger)]/30 bg-[var(--ds-status-danger-bg)] px-4 py-3 text-[var(--ds-status-danger)]">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 rounded-[var(--ds-radius-md)] border border-[var(--ds-status-success)]/30 bg-[var(--ds-status-success-bg)] px-4 py-3 text-[var(--ds-status-success)]">
            {success}
          </div>
        )}

        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-[var(--ds-accent-primary)]/20 border-t-[var(--ds-accent-primary)]"></div>
              <p className="mt-3 text-xs text-[var(--ds-text-muted)]">Loading team members...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center text-xs text-[var(--ds-text-muted)]">
              <p className="text-h2 font-medium">No team members yet</p>
              <p className="mt-2 text-meta">Start by inviting your first colleague.</p>
              <Button
                variant="primary"
                size="sm"
                onClick={openCreateForm}
                className="mt-4"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add Team Member
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow isHeader>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Access Tags</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead align="right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((teamUser) => {
                  const isCurrentUser = teamUser.id === currentUser?.id;
                  const flagBadges = ACCESS_FLAGS.filter((flag) => teamUser[flag.key]);

                  return (
                    <TableRow key={teamUser.id}>
                      <TableCell>
                        <div className="text-xs font-medium text-[var(--ds-text-primary)]">
                          {teamUser.firstName} {teamUser.lastName}
                        </div>
                        {teamUser.phone && (
                          <div className="text-xs text-[var(--ds-text-muted)]">{teamUser.phone}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        {teamUser.email}
                      </TableCell>
                      <TableCell>
                        <Badge variant={teamUser.role === 'tenant_admin' ? 'success' : 'neutral'} className="text-xs">
                          {teamUser.role === 'tenant_admin' ? <ShieldCheck className="h-3.5 w-3.5 mr-1" /> : <ShieldAlert className="h-3.5 w-3.5 mr-1" />}
                          {teamUser.role.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {flagBadges.length === 0 ? (
                          <span className="text-xs text-[var(--ds-text-muted)]">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {flagBadges.map((flag) => (
                              <Badge key={flag.key} variant="neutral" className="text-xs">
                                {flag.label}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={teamUser.isActive ? 'success' : 'neutral'} className="text-xs">
                          {teamUser.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {formatDateTime(teamUser.lastLoginAt)}
                      </TableCell>
                      <TableCell align="right">
                        <div className="flex justify-end gap-2">
                          <Tooltip content="Edit">
                            <Button
                              variant="icon"
                              size="sm"
                              type="button"
                              onClick={() => openEditForm(teamUser)}
                              className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          {!isCurrentUser && teamUser.isActive && (
                            <Tooltip content="Deactivate">
                              <Button
                                variant="icon"
                                size="sm"
                                type="button"
                                onClick={() => handleDeactivate(teamUser)}
                                className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-3xl rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {isEditing ? 'Edit Team Member' : 'Add Team Member'}
              </h2>
              <button
                type="button"
                onClick={handleFormClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FormLabel htmlFor="firstName" required>First name</FormLabel>
                  <Input
                    id="firstName"
                    type="text"
                    value={formState.firstName}
                    onChange={handleInputChange('firstName')}
                    className="h-8"
                    required
                  />
                </div>
                <div>
                  <FormLabel htmlFor="lastName" required>Last name</FormLabel>
                  <Input
                    id="lastName"
                    type="text"
                    value={formState.lastName}
                    onChange={handleInputChange('lastName')}
                    className="h-8"
                    required
                  />
                </div>
                <div>
                  <FormLabel htmlFor="email" required>Email</FormLabel>
                  <Input
                    id="email"
                    type="email"
                    value={formState.email}
                    onChange={handleInputChange('email')}
                    className="h-8"
                    required
                  />
                </div>
                <div>
                  <FormLabel htmlFor="phone">Phone (optional)</FormLabel>
                  <Input
                    id="phone"
                    type="text"
                    value={formState.phone}
                    onChange={handleInputChange('phone')}
                    className="h-8"
                  />
                </div>

                <div>
                  <FormLabel htmlFor="role">Role</FormLabel>
                  <Select
                    id="role"
                    value={formState.role}
                    onChange={handleInputChange('role')}
                    className="h-8"
                    disabled={editingSelf || (isEditing && formState.role === 'tenant_admin')}
                  >
                    {roleOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <FormLabel htmlFor="password">
                    {isEditing ? 'New password (optional)' : 'Password'}
                  </FormLabel>
                  <Input
                    id="password"
                    type="password"
                    value={formState.password}
                    onChange={handleInputChange('password')}
                    className="h-8"
                    placeholder={isEditing ? 'Leave blank to keep existing password' : ''}
                    {...(!isEditing && { required: true })}
                  />
                </div>
                <div>
                  <FormLabel htmlFor="confirmPassword">Confirm password</FormLabel>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={formState.confirmPassword}
                    onChange={handleInputChange('confirmPassword')}
                    className="h-8"
                    placeholder={isEditing ? 'Repeat new password if changing' : ''}
                    {...(!isEditing && { required: true })}
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    id="is-active"
                    type="checkbox"
                    checked={formState.isActive}
                    onChange={handleCheckboxChange('isActive')}
                    disabled={editingSelf}
                    className="h-4 w-4 text-[var(--ds-accent-primary)] border-[var(--ds-border-subtle)] rounded"
                  />
                  <FormLabel htmlFor="is-active" spacing={false} className="text-xs">
                    Account is active
                  </FormLabel>
                </div>
              </div>

              <div>
                <h3 className="text-h2 font-medium text-[var(--ds-text-primary)] mb-3">Access tags</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {ACCESS_FLAGS.map((flag) => (
                    <label key={flag.key} className="flex items-center gap-2 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-control-field-bg)] px-3 py-2 text-xs text-[var(--ds-text-primary)]">
                      <input
                        type="checkbox"
                        checked={formState[flag.key]}
                        onChange={handleCheckboxChange(flag.key)}
                        className="h-4 w-4 text-[var(--ds-accent-primary)] border-[var(--ds-border-subtle)] rounded"
                      />
                      {flag.label}
                    </label>
                  ))}
                </div>
              </div>

              {formState.role === 'tenant_user' && (
                <div>
                  <h3 className="text-h2 font-medium text-[var(--ds-text-primary)] mb-3">Permissions</h3>
                  <div className="flex items-center gap-2 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-control-field-bg)] px-3 py-2 text-xs text-[var(--ds-text-primary)]">
                    <input
                      type="checkbox"
                      id="can-access-certificates"
                      checked={formState.canAccessCertificates}
                      onChange={handleCheckboxChange('canAccessCertificates')}
                      className="h-4 w-4 text-[var(--ds-accent-primary)] border-[var(--ds-border-subtle)] rounded"
                    />
                    <FormLabel htmlFor="can-access-certificates" spacing={false} className="cursor-pointer">
                      Access Certificates
                    </FormLabel>
                  </div>
                  <p className="text-meta text-[var(--ds-text-muted)] mt-2">
                    Allow this user to access the certificates section
                  </p>
                </div>
              )}

              {isEditing && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-h2 font-medium text-[var(--ds-text-primary)]">Test Equipment</h3>
                    <Button
                      variant="outline"
                      size="sm"
                      type="button"
                      onClick={() => openEquipmentForm()}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Add Equipment
                    </Button>
                  </div>
                  {testEquipment.length === 0 ? (
                    <div className="text-xs text-[var(--ds-text-muted)] py-4 text-center border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] bg-[var(--ds-control-field-bg)]">
                      No test equipment added yet
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {testEquipment.map((equipment) => (
                        <div
                          key={equipment.id}
                          className="flex items-start justify-between p-3 border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] bg-[var(--ds-control-field-bg)]"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-[var(--ds-text-primary)]">
                                {equipment.equipmentType}
                              </span>
                              {equipment.model && (
                                <span className="text-xs text-[var(--ds-text-muted)]">
                                  {equipment.model}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-[var(--ds-text-muted)] space-y-0.5">
                              <div>Serial: {equipment.serialNumber}</div>
                              <div>Manufacturer: {equipment.manufacturer}</div>
                              {equipment.nextCalibrationDue && (
                                <div>
                                  Next calibration: {new Date(equipment.nextCalibrationDue).toLocaleDateString('en-GB')}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              variant="icon"
                              size="sm"
                              type="button"
                              onClick={() => openEquipmentForm(equipment)}
                              className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="icon"
                              size="sm"
                              type="button"
                              onClick={() => handleDeleteEquipment(equipment.id)}
                              className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving…' : isEditing ? 'Save changes' : 'Create user'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {equipmentFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {equipmentFormState.id ? 'Edit Test Equipment' : 'Add Test Equipment'}
              </h2>
              <button
                type="button"
                onClick={closeEquipmentForm}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleEquipmentSubmit} className="px-6 py-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <FormLabel htmlFor="equipmentType" required>Equipment Type</FormLabel>
                  <Select
                    id="equipmentType"
                    value={equipmentFormState.equipmentType}
                    onChange={handleEquipmentInputChange('equipmentType')}
                    className="h-8"
                    required
                  >
                    <option value="">Select equipment type</option>
                    <option value="Multifunction">Multifunction</option>
                    <option value="Continuity">Continuity</option>
                    <option value="Insulation Resistance">Insulation Resistance</option>
                    <option value="Earth loop">Earth loop</option>
                    <option value="RCD">RCD</option>
                    <option value="PAT">PAT</option>
                    <option value="Other">Other</option>
                  </Select>
                </div>

                <div>
                  <FormLabel htmlFor="serialNumber" required>Serial Number</FormLabel>
                  <Input
                    id="serialNumber"
                    type="text"
                    value={equipmentFormState.serialNumber}
                    onChange={handleEquipmentInputChange('serialNumber')}
                    className="h-8"
                    required
                  />
                </div>

                <div>
                  <FormLabel htmlFor="manufacturer" required>Manufacturer</FormLabel>
                  <Input
                    id="manufacturer"
                    type="text"
                    value={equipmentFormState.manufacturer}
                    onChange={handleEquipmentInputChange('manufacturer')}
                    className="h-8"
                    required
                  />
                </div>

                <div>
                  <FormLabel htmlFor="model">Model</FormLabel>
                  <Input
                    id="model"
                    type="text"
                    value={equipmentFormState.model}
                    onChange={handleEquipmentInputChange('model')}
                    className="h-8"
                  />
                </div>

                <div>
                  <FormLabel htmlFor="calibrationDate">Calibration Date</FormLabel>
                  <Input
                    id="calibrationDate"
                    type="date"
                    value={equipmentFormState.calibrationDate}
                    onChange={handleEquipmentInputChange('calibrationDate')}
                    className="h-8"
                  />
                </div>

                <div>
                  <FormLabel htmlFor="nextCalibrationDue">Next Calibration Due</FormLabel>
                  <Input
                    id="nextCalibrationDue"
                    type="date"
                    value={equipmentFormState.nextCalibrationDue}
                    onChange={handleEquipmentInputChange('nextCalibrationDue')}
                    className="h-8"
                  />
                </div>

                <div className="md:col-span-2">
                  <FormLabel htmlFor="notes">Notes</FormLabel>
                  <Textarea
                    id="notes"
                    value={equipmentFormState.notes}
                    onChange={handleEquipmentInputChange('notes')}
                    rows={3}
                    className="resize-y"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={closeEquipmentForm}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  type="submit"
                >
                  {equipmentFormState.id ? 'Update Equipment' : 'Add Equipment'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
    </TooltipProvider>
  );
}


